import { httpClient } from "@/infrastructure/http/http.service";
import { logError } from "@/infrastructure/observability/error-logging.service";
import type {
  UserList,
  ListResponse,
  RemoveFromListResponse,
  AddToListResponse,
} from "@/types/list.types";
import type { SsrClient } from "@/infrastructure/http/ssr-client.service";
import type { HttpClient } from "@/infrastructure/http/http-client.factory";

const baseUrl = "tb-api/list";

/**
 * Gets the user's lists
 * @param requestClient Browser HttpClient or policy-aware SsrClient.
 * @param idWork Optional work whose list membership should be returned.
 * @param idAmazon Optional edition whose list membership should be returned.
 * @returns A promise that resolves to an array of user lists.
 */
export async function getUserLists(
  requestClient: Pick<SsrClient | HttpClient, "get"> = httpClient,
  idWork?: number,
  idAmazon?: number,
): Promise<ListResponse> {
  try {
    const response = await requestClient.get<ListResponse>(
      `${baseUrl}/get-lists/`,
      { params: { idWork, idAmazon } },
    );

    return response.data;
  } catch (e) {
    logError(e, { context: "getUserLists" });

    return {
      userLists: [],
      listsSharedWithUser: [],
    };
  }
}

/**
 * Gets the current user's lists with membership information for an edition.
 * @param idWork Work containing the edition.
 * @param idAmazon Edition to check in each list.
 * @returns Lists and the matching list item identifiers, when present.
 */
export async function getListsForEdition(
  idWork: number,
  idAmazon: number,
): Promise<ListResponse> {
  return getUserLists(httpClient, idWork, idAmazon);
}

/**
 * Creates a list
 * @param title The list's title
 * @param isDefault Whether or not this is the user's default list for adding new works
 * @returns The newly created list or null on failure
 */
export async function createUserList(
  title: string,
  isDefault: boolean,
): Promise<UserList | null> {
  try {
    const response = await httpClient.post<UserList>(
      `${baseUrl}/create-list/`,
      {
        title: title.trim(),
        isDefault,
      },
      { acceptAnyStatus: true },
    );
    if (response.status === 200) return response.data;

    // Duplicate names and list-limit rejections are expected validation
    // outcomes. The form owns presenting those to the customer, so do not
    // report them as application errors.
    if (response.status === 400 || response.status === 409) return null;

    logError(
      new Error(`Create list returned unexpected status ${response.status}`),
      { context: "createUserList" },
    );
    return null;
  } catch (e) {
    logError(e, { context: "createUserList" });
    return null;
  }
}

/**
 * Removes a shared list from the current user's shared lists
 * @param idList the list to delete
 * @returns Returns true on success
 */
export async function deleteSharedList(idList: number): Promise<boolean> {
  try {
    const response = await httpClient.delete<boolean>(
      `${baseUrl}/shared/${idList}/`,
    );
    return response.data;
  } catch (e) {
    logError(e, { context: "deleteSharedList" });
    return false;
  }
}

/**
 * Adds a work to a user's wishlist
 * @param idList the list to add to, null for the default
 * @param idAmazon the edition
 * @returns Returns true on success
 */
export async function addToList(
  idList: number | null,
  idAmazon: number,
): Promise<AddToListResponse> {
  try {
    const response = await httpClient.post<AddToListResponse>(
      `${baseUrl}/add-to-list/`,
      {
        idList,
        idAmazon,
      },
    );
    return response.data;
  } catch (e) {
    return {
      idList: idList ?? 0,
      idListItem: 0,
      success: false,
      message: String(e),
      title: "unknown",
    };
  }
}

/**
 * Removes a work from a user's wishlist
 * @param idListItem the item to remove
 * @returns Returns true on success
 */
export async function removeFromList(
  idListItem: number,
): Promise<RemoveFromListResponse> {
  try {
    const response = await httpClient.delete<RemoveFromListResponse>(
      `${baseUrl}/remove-from-list/`,
      {
        params: { idListItem },
      },
    );
    return response.data;
  } catch (e) {
    return {
      success: false,
      message: String(e),
      idList: 0,
      title: "unknown",
    };
  }
}
